import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Save, Building2 } from 'lucide-react';

/**
 * Create/edit a real-estate agent. `agent` prop seeds the form when editing.
 * Calls `onSaved(agent)` with the persisted doc so callers can immediately
 * link the new agent to a property.
 */
export default function AgentFormModal({ open, onClose, onSaved, agent = null, prefillFromClient = null }) {
  const { api } = useAuth();
  const isEdit = !!agent?._id;

  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', notes: '', active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (agent) {
      setForm({
        name: agent.name || '',
        company: agent.company || '',
        phone: agent.phone || '',
        email: agent.email || '',
        notes: agent.notes || '',
        active: agent.active !== false,
      });
    } else if (prefillFromClient) {
      setForm({
        name: `${prefillFromClient.name_first || ''} ${prefillFromClient.name_last || ''}`.trim(),
        company: prefillFromClient.company_name || '',
        phone: prefillFromClient.phone_mobile || prefillFromClient.phone_work || '',
        email: prefillFromClient.email || '',
        notes: '',
        active: true,
      });
    } else {
      setForm({ name: '', company: '', phone: '', email: '', notes: '', active: true });
    }
    setError('');
  }, [open, agent, prefillFromClient]);

  if (!open) return null;

  const save = async () => {
    if (!form.name && !form.company) { setError('Enter at least an agent name or company.'); return; }
    setSaving(true); setError('');
    try {
      const url = isEdit ? `/agents/${agent._id}` : '/agents';
      const method = isEdit ? api.put : api.post;
      const { data } = await method(url, form);
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="agent-form-modal" className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#D35400]/10 flex items-center justify-center"><Building2 size={16} className="text-[#D35400]" /></div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">{isEdit ? `Edit agent ${agent.agent_no || ''}` : 'New managing agent'}</h2>
              <p className="text-[11px] text-slate-500">Auto-generates an ID like WRE0001 from the first letters of the company name.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Company *</label>
            <input
              data-testid="agent-modal-company"
              value={form.company}
              onChange={e => setForm({ ...form, company: e.target.value })}
              placeholder="e.g. Waikato Real Estate"
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Agent Name *</label>
            <input
              data-testid="agent-modal-name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Sarah Smith"
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Phone</label>
              <input data-testid="agent-modal-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Email</label>
              <input data-testid="agent-modal-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Notes</label>
            <textarea data-testid="agent-modal-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
          </div>
          {error && <p data-testid="agent-modal-error" className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
          <button onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Cancel</button>
          <button data-testid="agent-modal-save" onClick={save} disabled={saving} className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] disabled:opacity-50 flex items-center gap-1.5">
            <Save size={14} /> {saving ? 'Saving…' : (isEdit ? 'Update agent' : 'Create agent')}
          </button>
        </div>
      </div>
    </div>
  );
}
